
import { Component, Vue } from 'vue-property-decorator'
import { SelectOption } from '@/components/common/atoms/SelectBase.vue'

export type StudyData = {
  studyContentId: number
  questionId: number
  gradeName: string
  subjectName: string
  studyName: string
  sProblemSortNum: number
  questionSortNum: number
  answerFormat: string
  assignStatus: string
  progressDenominator: number
  progressNumerator: number
  isScored: boolean
  isPublic: boolean
}

export type StudySearchQuery = {
  gradeId?: number
  subjectId?: number
  studyName?: string
  sProblemSortNum?: number
  questionSortNum?: number
  answerFormat?: string
  assignStatus?: string[]
}

export type SubjectsType = {
  subjectCode: string
  subjectName: string
}

export type AssignStatusType = {
  statusText: string
  statusValue: string
}

export type ModalMarkerType = {
  notAssignedCountAll: number
  markers: ModalMarkerListType[]
}

export type ModalMarkerListType = {
  isChecked: boolean
  markerId: number
  bcbtCode: string
  subjectNames: string[]
  nickname: string
  assignedCountAll: number
  assignedCount: number
  assignCount: number
}

@Component
export default class StudentListApis extends Vue {
  /**
   * 学年一覧取得
   */
  protected async loadGrades() {
    const { data } = await Vue.prototype.$http.httpWithToken.get('/v1/grades', {
      params: { onlyEnableStudy: true },
    })
    return data.map((obj: { id: number; code: string; name: string; sort_num: number }) => {
      return { value: obj.id, text: obj.name }
    })
  }

  /**
   * 科目一覧取得
   */
  protected async loadSubjects(studyContentId?: number): Promise<SubjectsType[]> {
    const { data } = await Vue.prototype.$http.httpWithToken.get('/v1/subjects', {
      params: { studyContentId: studyContentId },
    })
    return data.map((obj: { id: number; code: string; name: string; sortNum: number }) => {
      return { value: obj.id, text: obj.name }
    })
  }

  /**
   * テスト一覧取得
   */
  protected async searchStudies(
    studySearchQuery: StudySearchQuery,
    onSuccess: (response: StudyData[]) => void,
    onError: (err: any) => void
  ): Promise<void> {
    await Vue.prototype.$http.httpWithToken
      .get('/v1/admin/manual_scoring_questions', {
        params: studySearchQuery,
      })
      .then((response: { data: StudyData[] }) => {
        onSuccess(response.data)
      })
      .catch((err: any) => {
        onError(err)
      })
  }

  /**
   * 状態一覧取得
   */
  protected async getAssignStatus(): Promise<AssignStatusType[]> {
    return [
      { statusText: '未振分', statusValue: 'NotAssigned' },
      { statusText: '振分済', statusValue: 'Assigned' },
      { statusText: '採点中', statusValue: 'Scoring' },
      { statusText: '採点済', statusValue: 'Scored' },
      { statusText: '完了', statusValue: 'Completion' },
    ]
  }

  /**
   * テスト種類取得
   */
  protected async getAnswerFormat(): Promise<SelectOption[]> {
    // TODO: 解答形式の種別仕様が決まり次第実装
    return [
      {
        value: 'Description',
        text: '記述式',
      },
      /*
      {
        value: 'Drawing',
        text: '作図',
      },
      */
    ]
  }

  /**
   * 手動採点結果公開
   */
  protected async scoringIsPublicUpdateApi(
    studyContentId: number,
    questionId: number,
    onSuccess: () => void,
    onError: (err: any) => void
  ): Promise<void> {
    await Vue.prototype.$http.httpWithToken
      .patch(`/v1/admin/scoring_is_public`, {
        studyContentId: studyContentId,
        questionId: questionId,
      })
      .then(() => {
        onSuccess()
      })
      .catch((err: any) => {
        onError(err)
      })
  }

  /**
   * モーダル用採点者一覧取得
   */
  protected async loadModalMarkerList(
    studyContentId: number,
    questionId: number,
    onSuccess: (response: ModalMarkerType) => void,
    onError: (err: any) => void
  ): Promise<void> {
    await Vue.prototype.$http.httpWithToken
      .get(`/v1/admin/marker_for_question`, {
        params: { studyContentId: studyContentId, questionId: questionId },
      })
      .then((response: { data: ModalMarkerType }) => {
        onSuccess(response.data)
      })
      .catch((err: any) => {
        onError(err)
      })
  }

  /**
   * 採点者振り分け件数：登録、更新
   */
  protected async markerAssignUpdateApi(
    modalStudyList: StudyData,
    markerAssignList: { markerId: number; assignmentNumber: number }[],
    onSuccess: () => void,
    onError: (err: any) => void
  ): Promise<void> {
    await Vue.prototype.$http.httpWithToken
      .patch(`/v1/admin/marker_assign`, {
        studyContentId: modalStudyList.studyContentId,
        questionId: modalStudyList.questionId,
        markerAssignList: markerAssignList,
      })
      .then(() => {
        onSuccess()
      })
      .catch((err: any) => {
        onError(err)
      })
  }

  /**
   * 採点者振り分け件数：削除（リセット）
   */
  protected async markerAssignDeleteApi(
    modalStudyList: StudyData,
    markerIds: number[],
    onSuccess: () => void,
    onError: (err: any) => void
  ): Promise<void> {
    await Vue.prototype.$http.httpWithToken
      .delete(`/v1/admin/marker_assign`, {
        data: {
          studyContentId: modalStudyList.studyContentId,
          questionId: modalStudyList.questionId,
          markerIds: markerIds,
        },
      })
      .then(() => {
        onSuccess()
      })
      .catch((err: any) => {
        onError(err)
      })
  }
}
